import type { ReactNode } from 'react'

import { t } from '../../../../common/i18n.js'
import type { BalanceTimeAuthStatus, BalanceTimeData } from '../../../../common/types/playtime.js'
import type { Language } from '../../../../common/types/i18n.js'
import { PageHeader } from '../../../components/page-header/page-header.js'
import { PageShell } from '../../../components/page-shell/page-shell.js'
import { Spinner } from '../../../components/spinner/spinner.js'
import { ViewBase, ViewBaseProps } from '../../base/base.js'
import { BalanceTimeConnect } from './balancetime-connect.js'
import { BalanceTimeFilter } from './balancetime-filter.js'
import { BalanceTimeSummaryCards } from './balancetime-summary.js'
import './balancetime.css'

// ── Props interface ─────────────────────────────────────────────────────────

type RangeType = 'week' | 'month' | 'quarter' | 'year'

export interface BalanceTimeViewProps {
    lang: Language

    // When true, render content only (no ViewBase/PageShell/PageHeader) — the
    // shared MeLayout supplies the chrome at /me/balancetime.
    embedded?: boolean
    base?: ViewBaseProps

    // Auth
    authStatus: BalanceTimeAuthStatus | null

    // Data
    data: BalanceTimeData | null
    isLoading: boolean
    loadError: string | null

    // Filter state
    rangeType: RangeType
    selectedUser: string

    // Callbacks (each change auto-loads — there is no explicit load button)
    onRangeChange: (range: RangeType) => void
    onOffsetChange: (delta: number) => void
    onOffsetSet: (offset: number) => void
    onUserChange: (userId: string) => void
}

// ── Main component (pure render) ────────────────────────────────────────────

export const BalanceTimeView = (props: BalanceTimeViewProps) => {
    const { lang, data, isLoading, loadError } = props
    const bt = t.playtime.balancetime

    // Renders content inside the standalone chrome (ViewBase + PageShell +
    // optional PageHeader), or bare when embedded under the shared MeLayout.
    const wrap = (children: ReactNode, header?: ReactNode) =>
        props.embedded ? (
            children
        ) : (
            <ViewBase {...(props.base ?? { notifications: undefined, navigation: undefined })}>
                <PageShell>
                    {header}
                    {children}
                </PageShell>
            </ViewBase>
        )

    // Auth screen
    if (props.authStatus && !props.authStatus.isAuthenticated) {
        return wrap(
            <BalanceTimeConnect
                authStatus={props.authStatus}
                lang={lang}
            />,
        )
    }

    // Loading
    if (isLoading && !data) {
        return wrap(
            <div className="balancetime balancetime--loading">
                <Spinner />
            </div>,
            <PageHeader
                title={t.playtime.hero.balancetimeTitle(lang)}
                subtitle={t.playtime.hero.balancetimeSubtitle(lang)}
            />,
        )
    }

    if (!data) {
        return wrap(<p>{bt.loadError(lang)}</p>)
    }

    return wrap(
        <div className="balancetime balancetime--loaded">
                <BalanceTimeFilter
                    rangeType={props.rangeType}
                    displayStartDate={data.displayStartDate}
                    displayEndDate={data.displayEndDate}
                    selectedUser={props.selectedUser}
                    users={data.users}
                    canSelectUser={data.canSelectUser}
                    lang={lang}
                    onRangeChange={props.onRangeChange}
                    onOffsetChange={props.onOffsetChange}
                    onOffsetSet={props.onOffsetSet}
                    onUserChange={props.onUserChange}
                />

                {loadError && (
                    <div className="balancetime__error">
                        <p>{loadError}</p>
                    </div>
                )}

                {!loadError && (
                    <BalanceTimeSummaryCards
                        summary={data.summary}
                        fte={data.fte}
                        displayEndDate={data.displayEndDate}
                        lang={lang}
                    />
                )}

                {isLoading && (
                    <div className="balancetime__loading-overlay">
                        <Spinner />
                    </div>
                )}
        </div>,
        <PageHeader
            title={t.playtime.hero.balancetimeTitle(lang)}
            subtitle={t.playtime.hero.balancetimeSubtitle(lang)}
        />,
    )
}
